import { useEffect, useRef } from 'react';
import Bezier from 'bezier-easing';
import { Tween, EasingFunction } from 'framer-motion';
import { tween, ColdSubscription } from 'popmotion';
import { interpolatePath } from 'd3-interpolate-path';
import { PathAnimation } from '../Types';

const tweenSlide = (
  targetD: string,
  step: number,
  node: SVGPathElement,
  transition?: Tween,
) => {
  node.setAttribute('d', targetD);

  return tween({
    duration: transition && transition.duration && (transition.duration * 1000),
    ease: Bezier(0, 0, 1, 1),
  }).start((t: number) => {
    node.setAttribute('transform', `translate(${step + (t * -step)}, 0)`);
  });
};

const tweenDraw = (
  targetD: string,
  node: SVGPathElement,
  transition?: Tween,
) => {
  node.setAttribute('stroke-dasharray', 'none');
  node.setAttribute('d', targetD);

  const lineLength = node.getTotalLength();

  return tween({
    duration: transition && transition.duration && (transition.duration * 1000),
    ease: transition && (transition.ease as EasingFunction),
  })
    .start((t: number) => {
      node.setAttribute('stroke-dasharray', `${lineLength * t},${lineLength}`);
    });
};

const tweenInterpolate = (
  sourceD: string,
  targetD: string,
  node: SVGPathElement,
  transition?: Tween,
) => {
  return tween({
    duration: transition && transition.duration && (transition.duration * 1000),
    ease: transition && (transition.ease as EasingFunction),
  })
    .pipe(interpolatePath(sourceD, targetD))
    .start((v: string) => {
      node.setAttribute('stroke-dasharray', 'none');
      node.setAttribute('d', v);
    });
};

export default (
  d: null | string,
  step: null | number,
  animate?: boolean | PathAnimation | Omit<PathAnimation, 'draw'>,
  transition?: Tween,
  getInitialD?: () => null | string,
) => {
  const ref = useRef<SVGPathElement>(null);
  const refTween = useRef<ColdSubscription>();

  useEffect(() => {
    if (ref.current) {
      const dAttrVal = ref.current.getAttribute('d') || (getInitialD ? getInitialD() : '');

      if (!animate || !dAttrVal || d === dAttrVal) {
        ref.current.setAttribute('d', d || '');
      } else if (dAttrVal && d !== dAttrVal) {
        if (refTween.current) {
          refTween.current.stop();
        }

        if (animate === 'slide' && step !== null) {
          refTween.current = tweenSlide(d || '', step, ref.current, transition);
        } else if (animate === 'draw') {
          refTween.current = tweenDraw(d || '', ref.current, transition);
        } else {
          refTween.current = tweenInterpolate(dAttrVal, d || '', ref.current, transition);
        }
      }
    }
  }, [animate, d, getInitialD, step, transition]);

  return ref;
};
